import { Module } from '../../types';
import { regexModule4QuestionBank } from '../questionBanks/regexModule4Questions';

export const regexModule4: Module = {
  id: 'module4',
  title: 'Advanced Patterns and Lookarounds',
  description:
    'Master alternation, non-capturing groups, and lookaround assertions for sophisticated pattern matching.',
  duration: '4 hours',
  content: `# Advanced Patterns and Lookarounds

## 🎯 Learning Objectives

By the end of this module, you will:
- Use alternation (|) to match multiple alternative patterns
- Understand and apply non-capturing groups (?:...)
- Master positive lookahead (?=...) and lookbehind (?<=...)
- Apply negative lookahead (?!...) and lookbehind (?<!...)
- Combine multiple advanced techniques for complex patterns
- Optimize regex performance with efficient pattern design

## 📚 Introduction

In this advanced module, we'll explore powerful regex features that allow you to create sophisticated patterns for complex matching scenarios. You'll learn alternation, non-capturing groups, and lookarounds - techniques that separate beginner regex users from experts.

## 🔀 Alternation: The OR Operator (|)

Alternation allows you to match one pattern OR another using the pipe symbol \`|\`.

### Basic Alternation

\`\`\`python
import re

text = "I like cats and dogs but not birds"

# Match either "cats" or "dogs"
pattern = r"cats|dogs"
matches = re.findall(pattern, text)
print(matches)  # ['cats', 'dogs']
\`\`\`

### Alternation with Groups

\`\`\`python
# Match file extensions
text = "files: image.jpg, document.pdf, script.py, photo.png"
pattern = r"\\w+\\.(jpg|pdf|png|py)"
matches = re.findall(pattern, text)
print(matches)  # ['jpg', 'pdf', 'py', 'png']

# Get the full filename and extension
pattern = r"(\\w+\\.(jpg|pdf|png|py))"
matches = re.findall(pattern, text)
print(matches)  # [('image.jpg', 'jpg'), ('document.pdf', 'pdf'), ...]
\`\`\`

### Complex Alternation

\`\`\`python
# Match different date formats
text = "Dates: 2024-01-15, 01/15/2024, Jan 15, 2024"
pattern = r"\\d{4}-\\d{2}-\\d{2}|\\d{2}/\\d{2}/\\d{4}|\\w{3} \\d{1,2}, \\d{4}"
matches = re.findall(pattern, text)
print(matches)  # ['2024-01-15', '01/15/2024', 'Jan 15, 2024']
\`\`\`

### Alternation Precedence and Grouping

\`\`\`python
# Be careful with alternation precedence
text = "specialist and specific words"

# WRONG: Matches "spec" in both words
wrong_pattern = r"spec|specific"
matches = re.findall(wrong_pattern, text)
print(f"Wrong order: {matches}")  # ['spec', 'spec']

# RIGHT: Longer patterns first
right_pattern = r"specific|spec"
matches = re.findall(right_pattern, text)
print(f"Right order: {matches}")  # ['specific', 'spec']

# BETTER: Use word boundaries
best_pattern = r"\\b(specific|spec)\\b"
matches = re.findall(best_pattern, text)
print(f"With boundaries: {matches}")  # ['spec']
\`\`\`

## 🚫 Non-Capturing Groups: (?:...)

Sometimes you need grouping for precedence but don't want to capture the content.

### Why Non-Capturing Groups?

\`\`\`python
# Problem: Too many captured groups
text = "Visit https://example.com or http://test.org"
pattern = r"(https|http)://(\\w+\\.\\w+)"
matches = re.findall(pattern, text)
print(matches)  # [('https', 'example.com'), ('http', 'test.org')]

# Solution: Non-capturing group for protocol
pattern = r"(?:https|http)://(\\w+\\.\\w+)"
matches = re.findall(pattern, text)
print(matches)  # ['example.com', 'test.org']
\`\`\`

### Grouping for Quantifiers

\`\`\`python
# Match repeated word pairs
text = "ha ha ha, ho ho ho, he he he"
pattern = r"(?:(\\w+) \\1 \\1)"  # \\1 refers to first captured group
matches = re.findall(pattern, text)
print(matches)  # ['ha', 'ho', 'he']

# Non-capturing group with quantifier
pattern = r"(?:very )+good"
text = "This is very very very good"
match = re.search(pattern, text)
print(match.group())  # 'very very very good'
\`\`\`

## 👀 Positive Lookahead: (?=...)

Lookahead assertions check what comes after the current position without consuming characters.

### Basic Positive Lookahead

\`\`\`python
# Match "Java" only if followed by "Script"
text = "I know Java, JavaScript, and JavaBeans"
pattern = r"Java(?=Script)"
matches = re.findall(pattern, text)
print(matches)  # ['Java']
\`\`\`

### Password Validation with Lookahead

\`\`\`python
# Password must contain: 8+ chars, digit, uppercase, lowercase
def validate_password(password):
    pattern = r"^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$"
    return bool(re.match(pattern, password))

print(validate_password("Password123"))  # True
print(validate_password("password"))     # False (no uppercase or digit)
print(validate_password("PASSWORD123"))  # False (no lowercase)
\`\`\`

### Multiple Conditions with Lookahead

\`\`\`python
# Find words that contain both 'a' and 'e'
text = "apple orange grape banana cherry"
pattern = r"\\b(?=\\w*a)(?=\\w*e)\\w+\\b"
matches = re.findall(pattern, text)
print(matches)  # ['apple', 'grape']
\`\`\`

## 🚫👀 Negative Lookahead: (?!...)

Negative lookahead ensures something does NOT follow the current position.

### Basic Negative Lookahead

\`\`\`python
# Match "Java" but NOT if followed by "Script"
text = "I know Java, JavaScript, and JavaBeans"
pattern = r"Java(?!Script)"
matches = re.findall(pattern, text)
print(matches)  # ['Java', 'Java'] (from Java and JavaBeans)
\`\`\`

### Excluding Specific Patterns

\`\`\`python
# Match numbers not followed by "px" or "%"
text = "Width: 100px, Height: 200, Opacity: 75%, Margin: 10"
pattern = r"\\d+(?![px%])"
matches = re.findall(pattern, text)
print(matches)  # ['200', '10']

# Find files that are NOT images
files = "document.pdf, image.jpg, script.py, photo.png, readme.txt"
pattern = r"\\w+\\.(?!jpg|png|gif|bmp)\\w+"
non_images = re.findall(pattern, files)
print(f"Non-image files: {non_images}")
\`\`\`

## 👀⬅️ Positive Lookbehind: (?<=...)

Lookbehind assertions check what comes before the current position.

### Basic Positive Lookbehind

\`\`\`python
# Match digits that come after "$"
text = "Price: $25, Discount: $5, Tax: 10%"
pattern = r"(?<=\\$)\\d+"
matches = re.findall(pattern, text)
print(matches)  # ['25', '5']
\`\`\`

### Complex Lookbehind

\`\`\`python
# Match words that come after "Mr." or "Ms."
text = "Hello Mr. Smith and Ms. Johnson"
pattern = r"(?<=Mr\\. |Ms\\. )\\w+"
matches = re.findall(pattern, text)
print(matches)  # ['Smith', 'Johnson']

# Extract values after specific keys
config = "host=localhost port=8080 debug=true timeout=30"
pattern = r"(?<=port=)\\d+"
port_value = re.search(pattern, config)
print(f"Port: {port_value.group()}")  # '8080'
\`\`\`

## 🚫👀⬅️ Negative Lookbehind: (?<!...)

Negative lookbehind ensures something does NOT precede the current position.

### Basic Negative Lookbehind

\`\`\`python
# Match digits not preceded by "$"
text = "Price: $25, Quantity: 3, Discount: $5"
pattern = r"(?<!\\$)\\d+"
matches = re.findall(pattern, text)
print(matches)  # ['3'] (25 and 5 are preceded by $)
\`\`\`

### Excluding Context

\`\`\`python
# Match "bank" but not when preceded by "river"
text = "The river bank is muddy, but the bank account is full"
pattern = r"(?<!river )\\bbank\\b"
matches = re.findall(pattern, text)
print(matches)  # ['bank'] (only the financial institution)

# Find words not preceded by articles
text = "a cat, an elephant, the dog, bird, fish"
pattern = r"(?<!a |an |the )\\b\\w+\\b"
words_without_articles = re.findall(pattern, text)
print(f"Words without articles: {words_without_articles}")
\`\`\`

## 🔗 Combining Advanced Techniques

### Complex URL Validation

\`\`\`python
# Match valid URLs with optional protocol
def is_valid_url(url):
    pattern = r"""
        ^(?:https?://)?          # Optional protocol
        (?=.{1,255}$)            # Total length check
        (?:www\\.)?              # Optional www
        (?!.*\\.\\.)             # No consecutive dots
        ([a-zA-Z0-9-]+\\.)+      # Domain parts
        [a-zA-Z]{2,}             # TLD
        (?:/[^\\s]*)?            # Optional path
        $
    """
    return bool(re.match(pattern, url, re.VERBOSE))

urls_to_test = [
    "https://example.com",
    "www.test.co.uk/path",
    "invalid..domain.com",
    "ftp://files.company.org",
    "subdomain.domain.com/path/to/page"
]

for url in urls_to_test:
    valid = is_valid_url(url)
    print(f"{'✓' if valid else '✗'} {url}")
\`\`\`

### Advanced Email Validation

\`\`\`python
# More sophisticated email validation
def validate_email(email):
    pattern = r"""
        ^(?=.{1,254}$)                    # Total length limit
        (?=.{1,64}@)                      # Local part length limit
        [a-zA-Z0-9._-]+                   # Local part
        (?<![._-])                        # Not ending with special chars
        @
        (?=.{1,253}$)                     # Domain length limit
        (?:[a-zA-Z0-9-]+\\.)+             # Domain parts
        [a-zA-Z]{2,}                      # TLD
        $
    """
    return bool(re.match(pattern, email, re.VERBOSE))

emails_to_test = [
    "user@example.com",
    "user.name@test.co.uk",
    "user-@domain.com",
    "very.long.email.address@very.long.domain.name.com",
    "user@domain"
]

for email in emails_to_test:
    valid = validate_email(email)
    print(f"{'✓' if valid else '✗'} {email}")
\`\`\`

## ⚡ Performance Considerations

### Quantifier Efficiency

\`\`\`python
# Inefficient: Catastrophic backtracking
# Avoid: (a+)+b when matching "aaaaaaaaaaaaaaaaaac"

# Efficient alternatives:
pattern1 = r"a+b"              # Simple quantifier
pattern2 = r"(?:a+)+b"         # Non-capturing group
pattern3 = r"a++b"             # Possessive quantifier (in some flavors)

# Benchmark different approaches
import time

text = "a" * 20 + "c"  # This will cause backtracking

# Efficient pattern
start = time.time()
result = re.search(r"a+b", text)
efficient_time = time.time() - start

print(f"Efficient pattern time: {efficient_time:.6f}s")
print(f"Match result: {result}")
\`\`\`

### Lookaround Optimization

\`\`\`python
# Less efficient: Multiple separate searches
def find_complex_pattern_slow(text):
    # Multiple regex calls
    has_digit = bool(re.search(r"\\d", text))
    has_upper = bool(re.search(r"[A-Z]", text))
    has_lower = bool(re.search(r"[a-z]", text))
    return has_digit and has_upper and has_lower

# More efficient: Single regex with lookaheads
def find_complex_pattern_fast(text):
    pattern = r"^(?=.*\\d)(?=.*[A-Z])(?=.*[a-z])"
    return bool(re.search(pattern, text))
\`\`\`

## 🛠️ Common Patterns and Use Cases

### 1. Balanced Parentheses (Simple Cases)
\`\`\`python
# Match balanced single-level parentheses
pattern = r"\\([^()]*\\)"
text = "Functions: func(), method(arg), nested((bad))"
matches = re.findall(pattern, text)
print(matches)  # ['()', '(arg)']
\`\`\`

### 2. Conditional Replacements
\`\`\`python
# Replace "color" with "colour" only in British context
text = "The color of the car and favorite color scheme"
# Replace "color" only if not followed by "ful" or "less"
pattern = r"color(?!ful|less)"
result = re.sub(pattern, "colour", text)
print(result)  # "The colour of the car and favorite colour scheme"
\`\`\`

### 3. Extract Quoted Strings
\`\`\`python
# Match quoted strings, handling escaped quotes
pattern = r'"(?:[^"\\\\]|\\\\.)*"'
text = 'Say "Hello \\\\"World\\\\"" and "Goodbye"'
matches = re.findall(pattern, text)
print(matches)  # ['"Hello \\\\"World\\\\""', '"Goodbye"']
\`\`\`

### 4. Context-Sensitive Number Extraction
\`\`\`python
# Extract monetary amounts but not percentages or measurements
text = "Price: $29.99, Discount: 15%, Weight: 2.5kg, Tax: $3.21"
pattern = r"(?<=\\$)\\d+\\.\\d{2}"
monetary_values = re.findall(pattern, text)
print(f"Monetary values: {monetary_values}")  # ['29.99', '3.21']
\`\`\`

## 📊 Key Concepts Summary

1. **Alternation (|)**:
   - Matches one pattern OR another
   - Use parentheses for precedence: \`(pattern1|pattern2)\`

2. **Non-Capturing Groups (?:...)**:
   - Group for precedence without capturing
   - More efficient when you don't need the captured text

3. **Positive Lookahead (?=...)**:
   - Matches if followed by pattern
   - Pattern is not consumed

4. **Negative Lookahead (?!...)**:
   - Matches if NOT followed by pattern
   - Useful for exclusions

5. **Positive Lookbehind (?<=...)**:
   - Matches if preceded by pattern
   - Pattern is not consumed

6. **Negative Lookbehind (?<!...)**:
   - Matches if NOT preceded by pattern
   - Useful for context-sensitive matching

## 🏆 Best Practices

- Use non-capturing groups when you don't need the captured text
- Be careful with lookarounds - they can impact performance
- Test complex patterns thoroughly with edge cases
- Use regex visualization tools for complex patterns
- Consider readability vs. performance trade-offs
- Use the VERBOSE flag for complex patterns with comments

## 🚀 What's Next?

You've mastered advanced patterns and lookarounds! In Module 5, you'll learn:

- **Real-World Applications**: Log parsing, data validation, text processing
- **Performance Optimization**: Writing efficient patterns for production
- **International Text**: Handling Unicode and different languages
- **Best Practices**: Maintainable and testable regex patterns
- **Integration**: Using regex effectively in larger applications

These final concepts will prepare you to use regex confidently in professional development!`,

  exercises: [
    {
      id: 'module4-exercise1',
      title: 'Basic Alternation',
      description: 'Create a pattern using alternation to match either "cat" or "dog"',
      type: 'regex',
      difficulty: 'easy',
      pattern: 'cat|dog',
      testCases: [
        { input: 'cat', expected: true, description: 'Matches cat' },
        { input: 'dog', expected: true, description: 'Matches dog' },
        { input: 'I have a cat', expected: true, description: 'Contains cat' },
        { input: 'My dog is friendly', expected: true, description: 'Contains dog' },
        { input: 'bird', expected: false, description: 'Neither cat nor dog' },
        { input: 'catalog', expected: true, description: 'Contains cat as substring' },
      ],
      hints: [
        'Use the pipe symbol | for alternation',
        'The pattern cat|dog matches either "cat" or "dog"',
        'Alternation checks from left to right',
      ],
    },
    {
      id: 'module4-exercise2',
      title: 'Non-Capturing Groups',
      description:
        'Create a pattern (?:https?|ftp)://\\w+ that matches protocols without capturing them',
      type: 'regex',
      difficulty: 'medium',
      pattern: '(?:https?|ftp)://\\w+',
      testCases: [
        { input: 'https://example', expected: true, description: 'HTTPS URL' },
        { input: 'http://test', expected: true, description: 'HTTP URL' },
        { input: 'ftp://files', expected: true, description: 'FTP URL' },
        { input: 'smtp://mail', expected: false, description: 'Unsupported protocol' },
        { input: 'Visit https://domain', expected: true, description: 'HTTPS URL in text' },
        { input: '://example', expected: false, description: 'Missing protocol' },
      ],
      hints: [
        'Use (?:...) for non-capturing groups',
        'https? means "http" or "https" (s is optional)',
        'The pattern groups protocols without capturing them',
      ],
    },
    {
      id: 'module4-exercise3',
      title: 'Positive Lookahead',
      description: 'Create a pattern \\d+(?=px) to match numbers followed by "px"',
      type: 'regex',
      difficulty: 'medium',
      pattern: '\\d+(?=px)',
      testCases: [
        { input: '100px', expected: true, description: 'Number followed by px' },
        { input: '50px margin', expected: true, description: 'Pixel value in text' },
        { input: '100', expected: false, description: 'Number without px' },
        { input: '100pt', expected: false, description: 'Number with different unit' },
        { input: 'width: 200px', expected: true, description: 'CSS width property' },
        { input: 'px100', expected: false, description: 'px before number' },
      ],
      hints: [
        'Use (?=...) for positive lookahead',
        'Lookahead checks what follows without consuming it',
        'The pattern \\d+(?=px) matches digits only if followed by "px"',
      ],
    },
    {
      id: 'module4-exercise4',
      title: 'Negative Lookahead',
      description: 'Create a pattern \\d+(?!%) to match numbers NOT followed by "%"',
      type: 'regex',
      difficulty: 'medium',
      pattern: '\\d+(?!%)',
      testCases: [
        { input: '100', expected: true, description: 'Number without %' },
        { input: '50%', expected: false, description: 'Percentage (has %)' },
        { input: '75px', expected: true, description: 'Number with px unit' },
        { input: 'Width: 200', expected: true, description: 'Number in text' },
        { input: 'Discount: 25%', expected: false, description: 'Percentage in text' },
        { input: '100 items', expected: true, description: 'Number followed by word' },
      ],
      hints: [
        'Use (?!...) for negative lookahead',
        'Negative lookahead ensures something does NOT follow',
        'The pattern \\d+(?!%) matches numbers not followed by %',
      ],
    },
    {
      id: 'module4-exercise5',
      title: 'Positive Lookbehind',
      description: 'Create a pattern (?<=\\$)\\d+ to match numbers preceded by "$"',
      type: 'regex',
      difficulty: 'hard',
      pattern: '(?<=\\$)\\d+',
      testCases: [
        { input: '$100', expected: true, description: 'Dollar amount' },
        { input: 'Price: $25', expected: true, description: 'Price in text' },
        { input: '100', expected: false, description: 'Number without $' },
        { input: '€50', expected: false, description: 'Different currency symbol' },
        { input: 'Cost $75 total', expected: true, description: 'Cost in sentence' },
        { input: 'USD100', expected: false, description: 'Currency code instead of symbol' },
      ],
      hints: [
        'Use (?<=...) for positive lookbehind',
        'Lookbehind checks what precedes without consuming it',
        'Use \\$ to match literal dollar sign (escape the special character)',
        'The pattern (?<=\\$)\\d+ matches digits only if preceded by "$"',
      ],
    },
    {
      id: 'module4-exercise6',
      title: 'Complex Password Validation',
      description:
        'Create a pattern ^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$ for password validation',
      type: 'regex',
      difficulty: 'hard',
      pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$',
      testCases: [
        {
          input: 'Password123',
          expected: true,
          description: 'Valid password with all requirements',
        },
        { input: 'password123', expected: false, description: 'Missing uppercase letter' },
        { input: 'PASSWORD123', expected: false, description: 'Missing lowercase letter' },
        { input: 'Password', expected: false, description: 'Missing digit' },
        { input: 'Pass123', expected: false, description: 'Too short (less than 8 chars)' },
        { input: 'MySecure1Pass', expected: true, description: 'Long valid password' },
      ],
      hints: [
        'Use ^ and $ to match entire string',
        'Multiple (?=.*...) lookaheads check for required character types',
        '(?=.*[a-z]) ensures at least one lowercase letter',
        '(?=.*[A-Z]) ensures at least one uppercase letter',
        '(?=.*\\d) ensures at least one digit',
        '.{8,} matches 8 or more characters',
      ],
    },
  ],

  quiz: {
    id: 'module4-quiz',
    title: 'Advanced Patterns and Lookarounds Quiz',
    questions: [
      {
        id: 'q1',
        question: 'What does the alternation operator | do in regex?',
        choices: [
          'Matches the beginning of a line',
          'Matches one pattern OR another',
          'Creates a character class',
          'Escapes special characters',
        ],
        correctAnswer: 1,
        explanation:
          'The alternation operator | acts like "OR" in regex, allowing you to match one pattern or another. For example, cat|dog matches either "cat" or "dog".',
      },
      {
        id: 'q2',
        question: 'What is the difference between (pattern) and (?:pattern)?',
        choices: [
          'No difference - they work the same way',
          '(?:pattern) is a capturing group, (pattern) is not',
          '(pattern) captures the matched text, (?:pattern) does not',
          '(?:pattern) only works with alternation',
        ],
        correctAnswer: 2,
        explanation:
          "(pattern) is a capturing group that stores the matched text for later use, while (?:pattern) is a non-capturing group that groups for precedence but doesn't capture the text. Non-capturing groups are more efficient when you don't need the captured content.",
      },
      {
        id: 'q3',
        question: 'What does positive lookahead (?=pattern) do?',
        choices: [
          'Matches pattern and consumes the characters',
          'Matches if followed by pattern, without consuming characters',
          'Matches if preceded by pattern',
          'Matches pattern only at the beginning of a line',
        ],
        correctAnswer: 1,
        explanation:
          "Positive lookahead (?=pattern) matches the current position only if it's followed by the specified pattern, but doesn't consume those characters. It's like \"peeking ahead\" without moving forward.",
      },
      {
        id: 'q4',
        question: 'When would you use negative lookahead (?!pattern)?',
        choices: [
          'To match pattern in reverse order',
          'To match only if NOT followed by pattern',
          'To make pattern case-insensitive',
          'To repeat pattern multiple times',
        ],
        correctAnswer: 1,
        explanation:
          "Negative lookahead (?!pattern) matches the current position only if it's NOT followed by the specified pattern. It's useful for exclusions, like matching numbers that aren't followed by \"%\".",
      },
      {
        id: 'q5',
        question: 'What does the pattern (?<=\\$)\\d+ match?',
        choices: [
          'Any digits followed by a dollar sign',
          'Any digits preceded by a dollar sign',
          'A literal "$" character followed by digits',
          'Digits with an optional dollar sign',
        ],
        correctAnswer: 1,
        explanation:
          'The pattern (?<=\\$)\\d+ uses positive lookbehind to match digits that are preceded by a dollar sign. The lookbehind (?<=\\$) checks for the "$" before the current position without consuming it.',
      },
      {
        id: 'q6',
        question:
          'In password validation, what does ^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$ check for?',
        choices: [
          'Exactly 8 characters with mixed case',
          'At least one lowercase, uppercase, digit, and minimum 8 characters',
          'Only letters and numbers, no special characters',
          'Password must start with uppercase and end with digit',
        ],
        correctAnswer: 1,
        explanation:
          'This pattern uses multiple positive lookaheads to ensure the password contains at least one lowercase letter (?=.*[a-z]), one uppercase letter (?=.*[A-Z]), one digit (?=.*\\d), and is at least 8 characters long (.{8,}).',
      },
    ],
    questionBank: regexModule4QuestionBank,
    questionsPerQuiz: 10,
    passingScore: 7,
  },

  resources: [
    {
      id: 'lookaround-guide',
      title: 'Lookaround Assertions Guide',
      type: 'document',
      url: '/resources/lookaround-guide.pdf',
      description: 'Comprehensive guide to positive and negative lookahead/lookbehind',
    },
    {
      id: 'alternation-patterns',
      title: 'Alternation Best Practices',
      type: 'document',
      url: '/resources/alternation-patterns.pdf',
      description: 'Advanced alternation techniques and performance tips',
    },
    {
      id: 'regex-performance',
      title: 'Regex Performance Optimization',
      type: 'link',
      url: 'https://www.regular-expressions.info/catastrophic.html',
      description: 'Guide to avoiding catastrophic backtracking and optimizing regex',
    },
    {
      id: 'regex-visualizer',
      title: 'Regex Visualizer',
      type: 'link',
      url: 'https://regexper.com/',
      description: 'Visualize complex regex patterns as railroad diagrams',
    },
  ],
};
